import { useQuery } from '@tanstack/react-query';
import { getPortfolioRegistry, PortfolioRegistryItem } from '../api/tax';

/**
 * Canonical hook for portfolio dropdown options across the tax-reporting
 * surface. Fetches the full registry once from `/v1/tax/portfolio-registry`
 * and surfaces it as ready-to-render `{value, label}[]` options.
 *
 * Replaces the prior per-view pattern that derived options from the current
 * page's items — which under server pagination only showed portfolios whose
 * rows happened to land on the current page.
 *
 * Cache policy: registry rows change rarely (a portfolio is added when the
 * operator onboards a new Coinbase account; deactivations happen at most a
 * few times a year). 10-minute staleTime keeps the network footprint tiny
 * while still picking up changes within a session if the operator adds a
 * portfolio elsewhere.
 */
const PORTFOLIO_REGISTRY_QUERY_KEY = ['tax', 'portfolio-registry'] as const;
const TEN_MINUTES_MS = 10 * 60 * 1000;

interface UsePortfolioRegistryResult {
  items: PortfolioRegistryItem[];
  options: { value: string; label: string }[];
  isLoading: boolean;
  isError: boolean;
}

export function usePortfolioRegistry(): UsePortfolioRegistryResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: PORTFOLIO_REGISTRY_QUERY_KEY,
    queryFn: () => getPortfolioRegistry(),
    staleTime: TEN_MINUTES_MS,
  });

  const items = data?.items ?? [];
  // Dropdown shows display_name (operator-friendly); filter value remains
  // portfolio_id (what every tax endpoint accepts as the portfolioId param).
  const options = items.map((p) => ({ value: p.portfolio_id, label: p.display_name }));

  return { items, options, isLoading, isError };
}
